import { ICSSVar } from './css';

export const FONT_CSS_VARS: ICSSVar[] = [
  {
    name: '--font-weight-default',
    value: 400
  },
  {
    name: '--font-weight-bold',
    value: 700
  },
  {
    name: '--font-weight-heavy',
    value: 900
  },
  {
    name: '--line-height-default',
    value: 1.25
  },
  {
    name: '--line-height-tall',
    value: 1.45
  }
];

enum FontWeight {
  Default = 'var(--font-weight-default)',
  Bold = 'var(--font-weight-bold)',
  Heavy = 'var(--font-weight-heavy)'
}

enum FontLineHeight {
  Default = 'var(--line-height-default)',
  Tall = 'var(--line-height-tall)'
}

enum Font {
  f40_heavy = 'f40_heavy',
  f28_heavy = 'f28_heavy',
  f24_heavy = 'f24_heavy',
  f20_heavy = 'f20_heavy',
  f20_bold = 'f20_bold',
  f18_bold = 'f18_bold',
  f16_bold_tall = 'f16_bold_tall',
  f14_bold_tall = 'f14_bold_tall',
  f12_bold_tall = 'f12_bold_tall',
  f40 = 'f40',
  f28 = 'f28',
  f20 = 'f20',
  f18 = 'f18',
  f16_tall = 'f16_tall',
  f14_tall = 'f14_tall',
  f12_tall = 'f12_tall',
  f14_bold_tall_upper = 'f14_bold_tall_upper',
  f13_bold_tall_upper = 'f13_bold_tall_upper',
  f11_bold_tall_upper = 'f11_bold_tall_upper'
}

export default Font;

const getFontCSS = (
  fontSize: number,
  fontWeight?: FontWeight,
  lineHeight?: FontLineHeight,
  isUppercase?: boolean,
  letterSpacing?: number
): string =>
  `
    font-size: ${fontSize}px;
    line-height: ${lineHeight ?? FontLineHeight.Default};
    font-weight: ${fontWeight ?? FontWeight.Default};
    ${isUppercase ? 'text-transform: uppercase;' : ''}
    ${letterSpacing ? `letter-spacing: ${letterSpacing}px;` : ''}
  `;

interface IFontConfig {
  key: Font;
  css: string;
}

export const FONT_CONFIG = new Map<Font, IFontConfig>([
  [
    Font.f40_heavy,
    {
      key: Font.f40_heavy,
      css: getFontCSS(40, FontWeight.Heavy, FontLineHeight.Default)
    }
  ],
  [
    Font.f40,
    {
      key: Font.f40,
      css: getFontCSS(40)
    }
  ],
  [
    Font.f28_heavy,
    {
      key: Font.f28_heavy,
      css: getFontCSS(28, FontWeight.Heavy)
    }
  ],
  [
    Font.f28,
    {
      key: Font.f28,
      css: getFontCSS(28)
    }
  ],
  [
    Font.f24_heavy,
    {
      key: Font.f24_heavy,
      css: getFontCSS(24, FontWeight.Heavy)
    }
  ],
  [
    Font.f20_bold,
    {
      key: Font.f20_bold,
      css: getFontCSS(20, FontWeight.Bold)
    }
  ],
  [
    Font.f20_heavy,
    {
      key: Font.f20_heavy,
      css: getFontCSS(20, FontWeight.Heavy)
    }
  ],
  [
    Font.f20,
    {
      key: Font.f20,
      css: getFontCSS(20)
    }
  ],
  [
    Font.f18_bold,
    {
      key: Font.f18_bold,
      css: getFontCSS(18, FontWeight.Bold)
    }
  ],
  [
    Font.f18,
    {
      key: Font.f18,
      css: getFontCSS(18)
    }
  ],
  [
    Font.f16_bold_tall,
    {
      key: Font.f16_bold_tall,
      css: getFontCSS(16, FontWeight.Bold, FontLineHeight.Tall)
    }
  ],
  [
    Font.f16_tall,
    {
      key: Font.f16_tall,
      css: getFontCSS(16, undefined, FontLineHeight.Tall)
    }
  ],
  [
    Font.f14_bold_tall,
    {
      key: Font.f14_bold_tall,
      css: getFontCSS(14, FontWeight.Bold, FontLineHeight.Tall)
    }
  ],
  [
    Font.f14_tall,
    {
      key: Font.f14_tall,
      css: getFontCSS(14, undefined, FontLineHeight.Tall)
    }
  ],
  [
    Font.f12_bold_tall,
    {
      key: Font.f12_bold_tall,
      css: getFontCSS(12, FontWeight.Bold, FontLineHeight.Tall)
    }
  ],
  [
    Font.f12_tall,
    {
      key: Font.f12_tall,
      css: getFontCSS(12, undefined, FontLineHeight.Tall)
    }
  ],
  [
    Font.f14_bold_tall_upper,
    {
      key: Font.f14_bold_tall_upper,
      css: getFontCSS(14, FontWeight.Bold, FontLineHeight.Tall, true, 1)
    }
  ],
  [
    Font.f13_bold_tall_upper,
    {
      key: Font.f13_bold_tall_upper,
      css: getFontCSS(13, FontWeight.Bold, FontLineHeight.Tall, true, 1)
    }
  ],
  [
    Font.f11_bold_tall_upper,
    {
      key: Font.f11_bold_tall_upper,
      css: getFontCSS(11, FontWeight.Bold, FontLineHeight.Tall, true, 1)
    }
  ]
]);

export const FONT_CONFIG_VALUES = Array.from(FONT_CONFIG).map(([k, v]) => v);
